import configment from '@adc/configment'
import type { GraphApiResponse } from '@adc/sdk/types'
import { ProductLine, headers as headersConstants } from '@adc/constants'
import {
  AdcFetch,
  PatchedResponse,
  BrowserFetchOptions,
  NodeFetchOptions,
} from './types'

export type GraphVariantOptions = {
  url?: string
  variables?: object
  operationName?: string
  query: string
}

export type GraphResponse<T> = {
  data: T
  errors: Error[]
}

const { ATTRIBUTION_HEADER } = headersConstants

//
// In all likelihood, we will use a dedicated graphql client for these requests, either one
// supplied by apollo, or another one, but as a basic means of interacting with the API
// correctly, this will suffice.
//
export default (fetchImplementation: AdcFetch) =>
  function forAuctionGraph<R extends string, T = unknown>(
    { query, variables, operationName }: GraphVariantOptions,
    opts?: BrowserFetchOptions | NodeFetchOptions
  ): Promise<PatchedResponse<GraphApiResponse<R, T>>> {
    // Per the spec, operationName and variables are optional fields. We prefer using standard application/json
    // over an extra suggested application/graphql content type.
    const body = { query, variables, operationName }

    const passthroughOpts = opts || {}
    const { headers = {}, requestTimeout } = passthroughOpts

    if (!headers[ATTRIBUTION_HEADER]) {
      headers[ATTRIBUTION_HEADER] = configment.get(
        'APP_THEME',
        ProductLine.AuctionCom
      )
    }

    // This looks slightly off at first glance, I'll admit, but there's a good reason. When we think about timeouts from
    // in any client/server architecture we have to consider both the client _and_ the server to allow both to abort
    // early and reduce unused word.
    //
    // Previously we had a client timeout which, worked as expected. If the API took to long it threw and apps carried
    // on without the data, and since promises are idempotent that settled promise wouldn't change, but, crucially,
    // the server kept processing and expected to return a response. By both running a timeout locally and sending
    // enough information to the API for it to abort (for example, terminating downstream network requests) we can keep
    // the system stable during unexpected system slowdowns.
    //
    // The follow-on here is that client should have a mechanism to server empty state; there ought to exit _some_
    // strategy for timeouts happen, because at some point they will (even if that is mostly during a sythentic soak
    // stress test whose goal is to determine the limits our system can operate within).
    //
    // The `requestTimeout` is for the local client timeout, and the header addition is a hint to the API for how long
    // it should attempt to fulfill the request before giving up.
    if (requestTimeout) {
      headers['Auction-Graph-Duration-Budget'] = requestTimeout
    }

    // @ts-ignore
    return fetchImplementation
      .fromService('RESI_GRAPH_API_URL')
      .post('/graphql', {
        ...(passthroughOpts || {}),
        // @ts-ignore If we are in the client we always want to enforce this and it's ignored by the server
        // implementation
        useProxy: false,
        credentials: 'include',
        requestTimeout,
        headers,
        body,
      })
  }
